import React, { useEffect, useMemo, useState } from "react";
import { cancelJob, deleteJob, retryJob, rollbackJob } from "../../../api/migration";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import Pagination from "../../../components/ui/Pagination";
import type { JobStatus, MigrationJob } from "../../../type";
import { extractApiError, formatDateTime } from "../helpers";
import JobProgress from "./JobProgress";
import JobStatusBadge from "./JobStatusBadge";

interface Props {
  jobs: MigrationJob[];
  count: number;
  page: number;
  setPage: (p: number) => void;
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  onOpenDetail: (job: MigrationJob) => void;
  showConfirm: (config: {
    title: string;
    message: React.ReactNode;
    confirmLabel: string;
    cancelLabel?: string;
    variant?: "primary" | "danger" | "warning";
    onConfirm: () => void;
    onCancel?: () => void;
  }) => void;
  showInfo: (config: {
    title: string;
    message: React.ReactNode;
    variant?: "success" | "info" | "warning";
  }) => void;
}

const PAGE_SIZE = 20;

const MigrationJobList: React.FC<Props> = ({
  jobs,
  count,
  page,
  setPage,
  isLoading,
  error,
  reload,
  onOpenDetail,
  showConfirm,
  showInfo,
}) => {
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  // Live clock so ETA calculations refresh while a job is running, even
  // between polling ticks. 1s cadence keeps the "Xs left" countdown
  // visibly moving without noticeable CPU cost.
  const [now, setNow] = useState(Date.now);
  const hasRunningJob = useMemo(
    () => jobs.some((j) => j.status === "running"),
    [jobs]
  );
  useEffect(() => {
    if (!hasRunningJob) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [hasRunningJob]);

  // Status tally across the CURRENT PAGE of jobs — not the whole server
  // set. Good enough for an at-a-glance "how busy is the queue" cue;
  // exact global counts would need a dedicated stats endpoint that isn't
  // worth the round-trip for a secondary UI.
  const statusCounts = useMemo(() => {
    const counts: Record<JobStatus, number> = {
      pending: 0,
      scheduled: 0,
      running: 0,
      succeeded: 0,
      failed: 0,
      canceled: 0,
    };
    jobs.forEach((j) => {
      counts[j.status] += 1;
    });
    return counts;
  }, [jobs]);

  const handleCancel = (job: MigrationJob) => {
    showConfirm({
      title: `Cancel job #${job.id}`,
      message: `Cancel this ${job.status} job? The selected rows will not be copied.`,
      confirmLabel: "Cancel job",
      variant: "danger",
      onConfirm: async () => {
        try {
          await cancelJob(job.id);
          showInfo({
            title: "Canceled",
            message: `Job #${job.id} was canceled.`,
            variant: "info",
          });
          await reload();
        } catch (err) {
          showInfo({
            title: "Cancel failed",
            message: extractApiError(err, "Cancel failed."),
            variant: "warning",
          });
        }
      },
    });
  };

  const handleRemove = (job: MigrationJob) => {
    showConfirm({
      title: `Delete job #${job.id}`,
      message: `Are you sure you want to permanently delete this ${job.status} job? This cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
      onConfirm: async () => {
        try {
          await deleteJob(job.id);
          showInfo({
            title: "Deleted",
            message: `Job #${job.id} was deleted.`,
            variant: "info",
          });
          await reload();
        } catch (err) {
          showInfo({
            title: "Delete failed",
            message: extractApiError(err, "Delete failed."),
            variant: "warning",
          });
        }
      },
    });
  };

  const handleRetry = (job: MigrationJob) => {
    showConfirm({
      title: `Retry job #${job.id}`,
      message:
        "Retry re-evaluates the original selection against beta right now and creates a new job. Rows already on product will be skipped.",
      confirmLabel: "Retry",
      variant: "primary",
      onConfirm: async () => {
        try {
          const fresh = await retryJob(job.id);
          showInfo({
            title: "Retry dispatched",
            message: `New job #${fresh.id} created.`,
            variant: "success",
          });
          await reload();
        } catch (err) {
          showInfo({
            title: "Retry failed",
            message: extractApiError(err, "Retry failed."),
            variant: "warning",
          });
        }
      },
    });
  };

  const handleRollback = (job: MigrationJob) => {
    showConfirm({
      title: `Rollback job #${job.id}`,
      message:
        "This will remove the LNP rows that were successfully migrated to Production in this job. Are you sure you want to rollback?",
      confirmLabel: "Rollback",
      variant: "danger",
      onConfirm: async () => {
        try {
          await rollbackJob(job.id);
          showInfo({
            title: "Rollback successful",
            message: `Job #${job.id} was rolled back.`,
            variant: "success",
          });
          await reload();
        } catch (err) {
          showInfo({
            title: "Rollback failed",
            message: extractApiError(err, "Rollback failed."),
            variant: "warning",
          });
        }
      },
    });
  };

  return (
    <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex flex-wrap items-baseline gap-3">
          <h3 className="text-base font-semibold text-gray-900">Jobs</h3>
          <span className="text-xs text-gray-500">
            {count.toLocaleString()} total
            {statusCounts.running > 0 && (
              <span className="ml-2 text-blue-700">
                · {statusCounts.running} running
              </span>
            )}
            {statusCounts.scheduled > 0 && (
              <span className="ml-2 text-indigo-700">
                · {statusCounts.scheduled} scheduled
              </span>
            )}
            {statusCounts.failed > 0 && (
              <span className="ml-2 text-red-700">
                · {statusCounts.failed} failed
              </span>
            )}
          </span>
        </div>
        <button
          type="button"
          onClick={() => void reload()}
          disabled={isLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1 text-xs text-blue-700 bg-white border border-blue-200 rounded hover:bg-blue-50 disabled:opacity-50"
        >
          {isLoading && <LoadingSpinner variant="inline" />}
          Refresh
        </button>
      </div>

      {error && (
        <div
          className="p-3 mb-3 text-sm text-red-800 border border-red-200 rounded-lg bg-red-50"
          role="alert"
        >
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        {/* First-load spinner only — once we have jobs, keep the table
            visible during polling refreshes so the header summary and
            progress bars stay readable without flicker. */}
        {isLoading && jobs.length === 0 ? (
          <LoadingSpinner label="Loading migration jobs" />
        ) : (
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-gray-500 uppercase bg-gray-50">
            <tr>
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Created by</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2">Scheduled</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Progress</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 && !isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-400">
                  No jobs yet.
                </td>
              </tr>
            )}
            {jobs.map((job) => (
              <tr key={job.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-900">{job.id}</td>
                <td className="px-3 py-2 text-gray-700">
                  {job.created_by_email ?? "(deleted)"}
                </td>
                <td className="px-3 py-2 text-gray-700">
                  {formatDateTime(job.created_at)}
                </td>
                <td className="px-3 py-2 text-gray-700">
                  {job.scheduled_at ? formatDateTime(job.scheduled_at) : "—"}
                </td>
                <td className="px-3 py-2">
                  <JobStatusBadge status={job.status} />
                </td>
                <td className="px-3 py-2">
                  <JobProgress job={job} now={now} />
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => onOpenDetail(job)}
                      className="px-2 py-1 text-xs text-blue-700 bg-white border border-blue-200 rounded hover:bg-blue-50"
                    >
                      Detail
                    </button>
                    {(job.status === "pending" ||
                      job.status === "scheduled") && (
                      <button
                        type="button"
                        onClick={() => handleCancel(job)}
                        className="px-2 py-1 text-xs text-red-700 bg-white border border-red-200 rounded hover:bg-red-50"
                      >
                        Cancel
                      </button>
                    )}
                    {(job.status === "failed" || job.status === "canceled") && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleRetry(job)}
                          className="px-2 py-1 text-xs text-blue-700 bg-white border border-blue-200 rounded hover:bg-blue-50"
                        >
                          Retry
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemove(job)}
                          className="px-2 py-1 text-xs text-red-700 bg-white border border-red-200 rounded hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </>
                    )}
                    {job.status === "succeeded" && (
                      <button
                        type="button"
                        onClick={() => handleRollback(job)}
                        className="px-2 py-1 text-xs text-red-700 bg-white border border-red-200 rounded hover:bg-red-50"
                      >
                        Rollback
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      <div className="pt-3 mt-3 border-t border-gray-100">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalCount={count}
          unit="jobs"
          onPageChange={setPage}
        />
      </div>
    </div>
  );
};

export default MigrationJobList;
